import { useStyletron } from '@thanx/uikit/theme'
import palette from 'constants/palette'
import { buildTranslate } from 'locales'
import React, { useEffect, useRef, useState } from 'react'
import type { ConsolidatedLocation } from '../mockLocations'

const t = buildTranslate('locations.location_list')

type Props = {
  location: ConsolidatedLocation
  onToggleOrdering: (location: ConsolidatedLocation) => void
  onViewDetails: (location: ConsolidatedLocation) => void
  disabled: boolean
}

const RowOverflowMenu = ({
  location,
  onToggleOrdering,
  onViewDetails,
  disabled,
}: Props) => {
  const [css] = useStyletron()
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement | null>(null)
  const triggerRef = useRef<HTMLButtonElement | null>(null)
  const menuRef = useRef<HTMLDivElement | null>(null)

  const closeMenu = () => {
    setOpen(false)
    // Return focus to the trigger so a keyboard user isn't stranded
    // on a hidden element after Escape / outside-click.
    triggerRef.current?.focus()
  }

  useEffect(() => {
    if (!open) return undefined
    const onDocClick = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) closeMenu()
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeMenu()
    }
    document.addEventListener('mousedown', onDocClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDocClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  // Focus the first menuitem when the menu opens so the keyboard
  // user lands inside the menu and the arrow-key handler below has
  // something to move FROM. Without this initial focus, ArrowDown
  // from the trigger does nothing because focus is still on the
  // trigger, not on a menuitem.
  useEffect(() => {
    if (!open) return
    const firstItem =
      menuRef.current?.querySelector<HTMLElement>('[role="menuitem"]')
    firstItem?.focus()
  }, [open])

  // WAI-ARIA menu pattern keyboard nav: ArrowDown/ArrowUp move focus
  // between menuitems, wrapping at boundaries. The menu only has two
  // items today but the pattern is the same at any cardinality, and
  // the same handler will keep working if a third action lands.
  // Home/End jump to the first/last for power users.
  const onMenuKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(e.key)) return
    e.preventDefault()
    const items = Array.from(
      menuRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? []
    )
    if (items.length === 0) return
    const currentIndex = items.indexOf(document.activeElement as HTMLElement)
    let nextIndex = currentIndex
    if (e.key === 'ArrowDown') {
      nextIndex = currentIndex < items.length - 1 ? currentIndex + 1 : 0
    } else if (e.key === 'ArrowUp') {
      nextIndex = currentIndex > 0 ? currentIndex - 1 : items.length - 1
    } else if (e.key === 'Home') {
      nextIndex = 0
    } else if (e.key === 'End') {
      nextIndex = items.length - 1
    }
    items[nextIndex]?.focus()
  }

  const label = location.orderingEnabled
    ? t('table.disable_ordering')
    : t('table.enable_ordering')

  // `not_configured` locations have no underlying provider configuration
  // to enable or disable — firing the PUT would just no-op against the
  // BE and confuse the operator with a non-event success toast. Disable
  // the menuitem so the action is unavailable.
  const toggleDisabled = disabled || !location.orderingConfigured

  // Stable id pairs the trigger's `aria-controls` with the menu
  // container's `id`. The WAI-ARIA Authoring Practices menu-button
  // pattern requires the pointer so assistive tech can programmatically
  // resolve the menu the trigger expands.
  const menuId = `row-actions-menu-${location.id}`

  return (
    <div ref={containerRef} className={css({ position: 'relative' })}>
      <button
        ref={triggerRef}
        type="button"
        aria-label={t('table.row_actions_label', { name: location.name })}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={menuId}
        onClick={() => setOpen(prev => !prev)}
        className={css({
          background: open ? palette.grey10 : 'none',
          border: 'none',
          padding: '6px 10px',
          borderRadius: '4px',
          color: palette.grey70,
          cursor: 'pointer',
          fontSize: '20px',
          lineHeight: 1,
        })}
      >
        <i className="fa fa-ellipsis-h" aria-hidden="true" />
      </button>
      {open && (
        <div
          id={menuId}
          ref={menuRef}
          role="menu"
          aria-label={t('table.row_actions_label', { name: location.name })}
          onKeyDown={onMenuKeyDown}
          className={css({
            position: 'absolute',
            top: '100%',
            right: '0',
            marginTop: '4px',
            minWidth: '200px',
            backgroundColor: palette.white,
            border: `1px solid ${palette.grey30}`,
            borderRadius: '4px',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
            zIndex: 10,
          })}
        >
          {/* WAI-ARIA menu pattern: menuitems use `aria-disabled`
              rather than the native HTML `disabled` attribute. Native
              disabled removes the element from the AT tab order, which
              breaks the menu's keyboard contract (operators using a
              screen reader can no longer hear that the action exists,
              only that it's been hidden). aria-disabled keeps the
              item discoverable while announcing the disabled state.
              The onClick is no-op'd when the flag is set so a
              keyboard activation falls through silently. */}
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              onViewDetails(location)
              setOpen(false)
            }}
            className={css({
              display: 'block',
              width: '100%',
              padding: '12px 16px',
              textAlign: 'left',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: palette.grey90,
              fontSize: '14px',
              ':hover': { backgroundColor: palette.grey05 },
              ':focus-visible': { outline: `2px solid ${palette.belizeHole}` },
            })}
          >
            {t('table.view_details')}
          </button>
          <button
            type="button"
            role="menuitem"
            aria-disabled={toggleDisabled || undefined}
            onClick={() => {
              if (toggleDisabled) return
              onToggleOrdering(location)
              setOpen(false)
            }}
            className={css({
              display: 'block',
              width: '100%',
              padding: '12px 16px',
              textAlign: 'left',
              background: 'none',
              border: 'none',
              cursor: toggleDisabled ? 'not-allowed' : 'pointer',
              color: toggleDisabled ? palette.grey50 : palette.grey90,
              fontSize: '14px',
              ':hover': toggleDisabled
                ? undefined
                : { backgroundColor: palette.grey05 },
              ':focus-visible': { outline: `2px solid ${palette.belizeHole}` },
            })}
          >
            {label}
          </button>
        </div>
      )}
    </div>
  )
}

export default RowOverflowMenu
